import React, { useCallback, useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { StarFilled, StarOutlined } from '@ant-design/icons';
import { Button, Flex, message, Spin, TabsProps } from 'antd';
import dayjs from 'dayjs';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import DocumentsContainer from '@totem/components/documents/report/documentsContainer';
import DocumentsTable from '@totem/components/documents/report/documentsTable';
import DocumentUploadButton from '@totem/components/documents/report/documentUploadButton';
import CommissioningReportContainer from '@totem/components/salesOrders/commissioning/CommissioningReportContainer';
import CommissioningReportFilters from '@totem/components/salesOrders/commissioning/CommissioningReportFilters';
import CommissioningReportTable from '@totem/components/salesOrders/commissioning/CommissioningReportTable';
import SalesOrderKeyLines from '@totem/components/salesOrders/salesOrderKey/SalesOrderKeyLines';
import EventsContainer, {
  containerConfiguration as eventContainerConfiguration,
} from '@totem/components/services/threats/eventsContainer/EventsContainer';
import EventsTable from '@totem/components/services/threats/eventsContainer/EventsTable';
import BuildingDecommissioningLayout from '@totem/components/ticketing/additional/buildingDecommissioning/BuildingDecommissioningLayout';
import TicketEvent from '@totem/components/ticketing/additional/event';
import Activities from '@totem/components/ticketing/details/activities/activities';
import ButtonCreateActivity from '@totem/components/ticketing/details/activities/buttonCreateActivity';
import ButtonCreateStaticActivity from '@totem/components/ticketing/details/activities/buttonCreateStaticActivity';
import ApprovalReport from '@totem/components/ticketing/details/approvals/approvalReport';
import ApprovalSection from '@totem/components/ticketing/details/approvals/approvalSection';
import ApprovalCreateButton from '@totem/components/ticketing/details/buttons/approvalCreateButton';
import ButtonCreateExpense from '@totem/components/ticketing/details/expenses/buttonCreateExpense';
import ExpensesSection from '@totem/components/ticketing/details/expenses/expensesSection';
import CancelButton from '@totem/components/ticketing/details/headerButtons/cancelButton';
import CloseButton from '@totem/components/ticketing/details/headerButtons/closeButton';
import RequestSummaryButton from '@totem/components/ticketing/details/headerButtons/requestSummaryButton';
import ResolveButton from '@totem/components/ticketing/details/headerButtons/resolveButton';
import TakeOwnershipButton from '@totem/components/ticketing/details/headerButtons/takeOwnershipButton';
import UpdateButton from '@totem/components/ticketing/details/headerButtons/updateButton';
import KeysTable from '@totem/components/ticketing/details/keys/keysTable';
import MessagesTable from '@totem/components/ticketing/details/messages/messagesTable';
import ButtonCreatePartyInvolved from '@totem/components/ticketing/details/partiesInvolved/buttonCreatePartyInvolved';
import PartiesInvolved from '@totem/components/ticketing/details/partiesInvolved/partiesInvolved';
import PartiesInvolvedAddDialog from '@totem/components/ticketing/details/partiesInvolved/partiesInvolvedAddDialog';
import ButtonEditProject from '@totem/components/ticketing/details/project/ButtonEditProject';
import ProjectDetails from '@totem/components/ticketing/details/project/projectDetails';
import QueueAssignments from '@totem/components/ticketing/details/queues/queueAssignments';
import RelatedTicketsTable from '@totem/components/ticketing/details/relatedTickets/relatedTicketsTable';
import ButtonCreateTask from '@totem/components/ticketing/details/tasks/buttonCreateTask';
import TasksSection from '@totem/components/ticketing/details/tasks/tasksSection';
import AddTasksFromTemplateButton from '@totem/components/ticketing/details/tasks/templates/AddTasksFromTemplateButton';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import TicketHeader from '@totem/components/ticketing/details/ticketHeader';
import ButtonCreateTimeEntry from '@totem/components/ticketing/details/timeEntry/buttonCreateTimeEntry';
import TimeTracking from '@totem/components/ticketing/details/timeEntry/timeTracking';
import { ticketPermissions } from '@totem/components/ticketing/permissions';
import TicketEntryDialog from '@totem/components/ticketing/ticket/ticketEntryDialog';
import { TicketQueryResult } from '@totem/components/ticketing/ticketing';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import CardBody from '@totem/components/ui_components/cards/CardBody';
import ContentLayout from '@totem/components/ui_components/page/ContentLayout';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import { statisticsStyles } from '@totem/components/ui_components/widgets/statistic/IBStatistic';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { Breadcrumb } from '@totem/types/breadcrumb';
import { ForeignKey } from '@totem/types/common';
import { isNotNull, isNull } from '@totem/utilities/common';
import {
  SALES_ORDER_ENDPOINT,
  TICKET_ENDPOINT,
} from '@totem/utilities/endpoints';

/* Tabs:
  Activity
  Parties Involved
  Queue Assignments
  Documents
  Project
  Tasks
  Building Decommissioning
  Approvals
  Time Tracking
  Expenses
  Related Tickets
  Event
  Messages
  Keys
  Sales Orders
  Commissioning
*/

const styles = {
  icon: {
    fontSize: '36px',
  },
  ticketNumber: {
    fontSize: '36px',
    marginLeft: '0.5em',
  },
};

const TicketDetailsLayout = () => {
  const location = useLocation();
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [selectedTab, setSelectedTab] = useState<string>('Activity');
  const [selectedSubTab, setSelectedSubTab] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [defaultTabSet, setDefaultTabSet] = useState<boolean>(false);
  const {
    ticket: ticketData,
    loading: isLoading,
    onUpdateTicket,
    action,
    onAction,
  } = useContext(TicketContext);

  const deleteKey = (key: ForeignKey) => {
    fetch(`${TICKET_ENDPOINT}/${ticketData.ticket.id}/keys`, {
      method: 'DELETE',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify({
        system: key.system,
        instance: key.instance,
        key: key.key,
      }),
    }).then((response) => {
      if (response.status !== 200) {
        message.error('Key delete failed!');
        return;
      }
      onAction('refresh', '');
    });
  };

  const sendFavoritesUpdate = useCallback(
    async (ticketId: string, method: string) => {
      let httpMethod = 'POST';

      if (isSending) {
        return;
      }

      if (method === 'DELETE') {
        httpMethod = 'DELETE';
      }

      fetch(`${TICKET_ENDPOINT}/${ticketId}/favorites`, {
        method: httpMethod,
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((response) => {
          if (response.status === 200) {
            onAction('refresh', '');
          } else {
            message.error('Favorites update failed!');
          }
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );

  const getBreadcrumbLabel = () => {
    if (ticketData !== null) {
      if (ticketData.ticket !== null) {
        return ticketData.ticket.number;
      }
    }
    return '';
  };

  const updateSelectedTab = (newTab: string) => {
    switch (newTab) {
      case 'Time Tracking':
        setSelectedSubTab('gridEntry');
        break;
      default:
        setSelectedSubTab('');
    }
    setSelectedTab(newTab);
  };

  const getBreadcrumbs = (breadcrumbsIn: Breadcrumb) => {
    const breadcrumbs = [breadcrumbsIn];

    const pathSegments = location.pathname.split('/');
    const hasOptimize = pathSegments.includes('optimize');

    if (hasOptimize) {
      breadcrumbs.push({
        label: 'Optimize',
        link: '/dashboard/optimize2',
      });
    } else {
      breadcrumbs.push({
        label: 'Tickets',
        link: '/dashboard/ticketing',
      });
    }

    breadcrumbs.push({
      label: `${getBreadcrumbLabel()}`,
    });

    return breadcrumbs;
  };

  const handleTicketUpdated = (updatedTicket: TicketQueryResult) => {
    onUpdateTicket(updatedTicket);

    onAction('refresh', '');
  };

  const getEventCount = () => {
    let count = 0;
    if (
      ticketData !== null &&
      ticketData.ticket !== null &&
      ticketData.ticket.keys !== null
    ) {
      for (let idx = 0; idx < ticketData.ticket.keys.length; idx++) {
        if (
          ticketData.ticket.keys[idx].system === 'Event Management' &&
          ticketData.ticket.keys[idx].instance === 'IBD'
        ) {
          count = count + 1;
        }
      }
    }
    return count;
  };

  const getEventId = () => {
    if (
      ticketData !== null &&
      ticketData.ticket !== null &&
      ticketData.ticket.keys !== null
    ) {
      for (let idx = 0; idx < ticketData.ticket.keys.length; idx++) {
        if (
          ticketData.ticket.keys[idx].system === 'Event Management' &&
          ticketData.ticket.keys[idx].instance === 'IBD'
        ) {
          return ticketData.ticket.keys[idx].key;
        }
      }
    }
    return null;
  };

  const sortTabItemsWithExtras = (
    tabItems: TabsProps['items'],
    desiredOrder: string[],
  ): TabsProps['items'] => {
    const tabItemMap = new Map(tabItems.map((item) => [item.key, item]));

    const sortedTabItems = desiredOrder
      .map((key) => tabItemMap.get(key))
      .filter((item): item is NonNullable<typeof item> => !!item);

    const remainingTabs = tabItems.filter(
      (item) => !desiredOrder.includes(item.key),
    );

    return [...sortedTabItems, ...remainingTabs];
  };

  const getTabItems = (): TabsProps['items'] => {
    const tabItems: TabsProps['items'] = [
      {
        key: 'Activity',
        label: 'Activity',
        children: <Activities />,
      },
      {
        key: 'PartiesInvolved',
        label: 'Parties Involved',
        children: (
          <PartiesInvolved
            ticket={ticketData}
            onTicketUpdated={handleTicketUpdated}
          />
        ),
      },
      {
        key: 'QueueAssignments',
        label: 'Queue Assignments',
        children: (
          <QueueAssignments
            ticket={ticketData}
            onTicketUpdated={handleTicketUpdated}
          />
        ),
      },
      {
        key: 'Documents',
        label: 'Documents',
        children: <DocumentsTable />,
      },
    ];

    if (
      permissions.internal.hasPermissions(userProfile) &&
      isNotNull(ticketData) &&
      isNotNull(ticketData.ticket) &&
      (ticketData.ticket.type === 'Project' ||
        ticketData.ticket.type === 'Site Project')
    ) {
      tabItems.push({
        key: 'Project',
        label: 'Project',
        children: <ProjectDetails />,
      });
    }

    //if (permissions.internal.hasPermissions(userProfile)) {
    // eslint-disable-next-line no-lone-blocks
    {
      tabItems.push({
        key: 'Tasks',
        label: 'Tasks',
        children: <TasksSection />,
      });
    }

    if (
      permissions.internal.hasPermissions(userProfile) &&
      isNotNull(ticketData) &&
      isNotNull(ticketData.ticket) &&
      ticketData.ticket.category === 'Decommission' &&
      ticketData.ticket.subCategory === 'Building' &&
      ticketData.building.id !== EMPTY_ID
    ) {
      tabItems.push({
        key: 'Building Decommissioning',
        label: 'Building Decommissioning',
        children: <BuildingDecommissioningLayout ticket={ticketData} />,
      });
    }

    if (
      ticketPermissions.approvalViewer.hasPermissions(
        userProfile,
        ticketData,
        null,
        null,
      ) &&
      (isNotNull(ticketData.ticket.approvalRequests) ||
        ticketData.ticket.type === 'Project')
    ) {
      tabItems.push({
        key: 'Approvals',
        label: 'Approvals',
        children: (
          <ApprovalReport
            approvalRequests={ticketData.ticket.approvalRequests}
          />
        ),
      });
    }

    if (permissions.internal.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'Time Tracking',
        label: 'Time Tracking',
        children: (
          <TimeTracking
            selectedTab={selectedSubTab}
            onTabSelect={setSelectedSubTab}
          />
        ),
      });
    }

    if (permissions.internal.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'Expenses',
        label: 'Expenses',
        children: (
          <ExpensesSection
            expenses={
              isNotNull(ticketData) &&
              isNotNull(ticketData.ticket.extras) &&
              isNotNull(ticketData.ticket.extras.expenses)
                ? ticketData.ticket.extras.expenses
                : []
            }
          />
        ),
      });
    }

    if (permissions.internal.hasPermissions(userProfile)) {
      tabItems.push({
        key: 'RelatedTickets',
        label: (
          <span>
            Related Tickets (
            {isNotNull(ticketData.relatedTickets)
              ? ticketData.relatedTickets.length
              : 0}
            )
          </span>
        ),
        children: (
          <RelatedTicketsTable
            ticket={ticketData}
            refetch={() => onAction('refresh', '')}
          />
        ),
      });
    }

    if (getEventCount() === 1) {
      tabItems.push({
        key: 'Event',
        label: 'Event',
        children: <TicketEvent eventId={getEventId()} />,
      });
    }

    if (getEventCount() > 1) {
      tabItems.push({
        key: 'Events',
        label: 'Events',
        children: (
          <InputContainer
            componentKey={'ticket.events'}
            componentLabel={'Ticket: Events'}
            filterConfig={eventContainerConfiguration}
            defaultSort={{
              field: 'eventTime',
              direction: 'descend',
            }}
            staticFilters={[
              {
                field: 'reference',
                values: [`Ticketing:IBD:${ticketData.ticket.id}`],
              },
            ]}
          >
            <EventsContainer>
              <CardBody>
                <EventsTable />
              </CardBody>
            </EventsContainer>
          </InputContainer>
        ),
      });
    }

    if (
      permissions.internal.hasPermissions(userProfile) &&
      isNotNull(ticketData.ticket.messages) &&
      ticketData.ticket.messages.length > 0
    ) {
      tabItems.push({
        key: 'Messages',
        label: (
          <span>
            Messages (
            {isNotNull(ticketData.ticket.messages)
              ? ticketData.ticket.messages.length
              : 0}
            )
          </span>
        ),
        children: <MessagesTable />,
      });
    }

    if (
      permissions.internal.hasPermissions(userProfile) &&
      isNotNull(ticketData.ticket.keys) &&
      ticketData.ticket.keys.length > 0
    ) {
      tabItems.push({
        key: 'Keys',
        label: 'Keys',
        children: (
          <KeysTable
            keys={ticketData.ticket.keys}
            onDelete={
              permissions.systemAdministrator.hasPermissions(userProfile)
                ? deleteKey
                : undefined
            }
          />
        ),
      });
    }

    if (
      permissions.internal.hasPermissions(userProfile) &&
      isNotNull(ticketData.ticket.keys)
    ) {
      const salesOrderKey = ticketData.ticket.keys.find(
        (chk) =>
          chk.system === 'Contracts' &&
          chk.instance === 'NETSUITE_SALES_ORDER_KEY',
      );

      if (isNotNull(salesOrderKey) && salesOrderKey.key !== '') {
        tabItems.push({
          key: 'Sales Order Lines',
          label: 'Sales Order Lines',
          children: <SalesOrderKeyLines salesOrderKey={salesOrderKey.key} />,
        });

        tabItems.push({
          key: 'Commissioning',
          label: 'Commissioning',
          children: (
            <CommissioningReportContainer
              endpoint={`${SALES_ORDER_ENDPOINT}/${salesOrderKey.key}/commissioningReport`}
            >
              <CommissioningReportFilters />
              <CommissioningReportTable />
            </CommissioningReportContainer>
          ),
        });
      }
    }

    if (
      ticketData.ticket.type === 'Project' ||
      ticketData.ticket.type === 'Site Project'
    ) {
      const desiredOrder = [
        'Project',
        'Tasks',
        'Time Tracking',
        'Activity',
        'PartiesInvolved',
        'Approvals',
        'Contract Lines',
        'Commissioning',
        'QueueAssignments',
        'Documents',
        'RelatedTickets',
        'Keys',
      ];

      const sortedTabItems = sortTabItemsWithExtras(tabItems, desiredOrder);

      if (!defaultTabSet && sortedTabItems.length > 0) {
        setSelectedTab(sortedTabItems[0].key);

        setDefaultTabSet(true);
      }

      return sortedTabItems;
    }

    if (!defaultTabSet && tabItems.length > 0) {
      setSelectedTab(tabItems[0].key);

      setDefaultTabSet(true);
    }
    return tabItems;
  };

  const getTabExtraContent = () => {
    switch (selectedTab) {
      case 'Time Tracking':
        if (
          selectedSubTab === 'report' &&
          permissions.internal.hasPermissions(userProfile)
        ) {
          return <ButtonCreateTimeEntry />;
        }
        return null;
      case 'Activity':
        return <ButtonCreateActivity />;
      case 'Approvals':
        if (
          ticketPermissions.approvalViewer.hasPermissions(
            userProfile,
            ticketData,
            null,
            null,
          )
        ) {
          return <ApprovalCreateButton />;
        }
        return null;
      case 'PartiesInvolved':
        return (
          <>
            <ButtonCreatePartyInvolved />
          </>
        );
      case 'QueueAssignments':
        return (
          <ButtonCreateStaticActivity
            activityId={'00777755f3329dc123ba0005'}
            title={'Add Queue Assignment'}
          />
        );
      case 'Tasks':
        if (
          ticketPermissions.taskEditor.hasPermissions(
            userProfile,
            ticketData,
            null,
          )
        ) {
          return (
            <>
              <IBTooltip content={ticketPermissions.taskEditor.tooltip}>
                {permissions.internal.hasPermissions(userProfile) && (
                  <span style={{ marginRight: '1rem' }}>
                    <AddTasksFromTemplateButton />
                  </span>
                )}
                <ButtonCreateTask />
              </IBTooltip>
            </>
          );
        }
        return null;
      case 'Expenses':
        if (permissions.internal.hasPermissions(userProfile)) {
          return <ButtonCreateExpense />;
        }
        return null;
      case 'Project':
        if (
          ticketPermissions.projectEditor.hasPermissions(
            userProfile,
            ticketData,
            null,
          )
        ) {
          return (
            <>
              <IBTooltip content={ticketPermissions.projectEditor.tooltip}>
                <ButtonEditProject />
              </IBTooltip>
            </>
          );
        }
        return null;
      case 'Documents':
        return (
          <Flex justify={'flex-end'}>
            <div>
              <DocumentUploadButton />
            </div>
          </Flex>
        );
      case 'RelatedTickets':
        return (
          <Button
            type={'primary'}
            onClick={() => onAction('create-child-ticket', '')}
          >
            Create Child Ticket
          </Button>
        );
      default:
        return null;
    }
  };

  const getFavoritesIcon = () => {
    if (isNotNull(ticketData) && isNotNull(ticketData.ticket.favorites)) {
      const foundIdx = ticketData.ticket.favorites.findIndex(
        (chk) => chk === userProfile.id,
      );
      if (foundIdx >= 0) {
        return (
          <Button
            disabled={isNull(ticketData)}
            title={'Remove Favorite'}
            shape={'square'}
            size={'small'}
            onClick={() => sendFavoritesUpdate(ticketData.ticket.id, 'DELETE')}
            style={statisticsStyles.warning}
            icon={<StarFilled style={{ color: '#FFC107' }} />}
          >
            Favorite
          </Button>
        );
      }
    }

    return (
      <Button
        disabled={isNull(ticketData)}
        title={'Favorite'}
        shape={'square'}
        size={'small'}
        onClick={() => sendFavoritesUpdate(ticketData.ticket.id, 'ADD')}
        icon={<StarOutlined style={{ color: '#BBBBBB' }} />}
      >
        Favorite
      </Button>
    );
  };

  return (
    <ContentLayout
      breadcrumbs={(breadcrumbs) => getBreadcrumbs(breadcrumbs[0])}
    >
      {ticketData !== null && (
        <IBCard
          isCovered
          title={
            <Flex justify={'flex-start'} align={'center'}>
              <IBDIcon name={ticketData.ticket.type} style={styles.icon} />
              <div style={styles.ticketNumber}>{ticketData.ticket.number}</div>
            </Flex>
          }
          extra={
            <Flex justify={'flex-end'} align={'center'}>
              <div style={{ ...styles.ticketNumber, marginRight: '16px' }}>
                {ticketData.ticket.internalOnly && <b>INTERNAL</b>}
              </div>
              <div style={styles.ticketNumber}>
                <Button
                  title={'Refresh'}
                  size={'small'}
                  shape={'square'}
                  onClick={() => onAction('refresh', null)}
                  icon={
                    <IBDIcon
                      style={{
                        color: '#BBBBBB',
                      }}
                      name={'Refresh'}
                    />
                  }
                >
                  Refresh
                </Button>
              </div>
              <div style={styles.ticketNumber}>{getFavoritesIcon()}</div>
              <UpdateButton buttonProps={{ size: 'small' }} />
              <TakeOwnershipButton buttonProps={{ size: 'small' }} />
              <ResolveButton buttonProps={{ size: 'small', type: 'default' }} />
              <CloseButton buttonProps={{ size: 'small' }} />
              <CancelButton buttonProps={{ size: 'small', type: 'default' }} />
              <RequestSummaryButton />
            </Flex>
          }
        >
          <TicketHeader ticket={ticketData} />
          <br />
          <ApprovalSection />
          <br />
          <DocumentsContainer
            documentReference={{
              regionId: ticketData.ticket.regionId,
              buildingId: ticketData.ticket.buildingId,
              controlSystemId: ticketData.ticket.controlSystemId,
              objectReference: {
                type: 'Ticket',
                id: ticketData.ticket.id,
                name: ticketData.ticket.number,
                description: ticketData.ticket.summary,
                createdBy: userProfile.email,
                createdAt: dayjs().toISOString(),
              },
            }}
            documentReferenceInternal={ticketData.ticket.internalOnly}
          >
            <IBTabs
              defaultActiveKey={getTabItems()[0].key}
              items={getTabItems()}
              onChange={(newKey) => updateSelectedTab(newKey)}
              tabBarExtraContent={getTabExtraContent()}
            />
          </DocumentsContainer>
        </IBCard>
      )}

      {ticketData === null && isLoading && <Spin />}
      {action === 'show-party-involved-entry' && <PartiesInvolvedAddDialog />}
      {action === 'create-child-ticket' && (
        <TicketEntryDialog
          onClose={(doRefresh) => {
            if (doRefresh) {
              onAction('refresh', '');
            } else {
              onAction('', '');
            }
          }}
          visible={action === 'create-child-ticket'}
          regionId={ticketData.ticket.regionId}
          buildingId={ticketData.ticket.buildingId}
          controlSystemId={ticketData.ticket.controlSystemId}
          deviceId={ticketData.ticket.deviceId}
          relatedTickets={[
            {
              relationship: 'Parent',
              objectType: 'Ticket',
              id: ticketData.ticket.id,
              name: ticketData.ticket.number,
            },
          ]}
          navigateToNewTicket={false}
          partyInvolvedTypes={['Assignee']}
        />
      )}
    </ContentLayout>
  );
};

export default TicketDetailsLayout;
